import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { authenticationService } from "../api/authentication.service";
import { userService } from "../api/user.service";
import { UserContext } from "./UserContext";
import Modal from "../components/elements/Modal";

const TOUR_STEPS = [
	{
		id: "new-pedigree-nav-item",
		path: null,
		title: "Open New Pedigree",
		description:
			"Click New Pedigree in the left menu to open the live builder yourself.",
		autoNavigate: false,
		advanceOnTargetClick: true,
	},
	{
		id: "pedigree-name-input",
		path: "/new-pedigree",
		title: "Name the pedigree",
		description:
			"Enter a memorable report name here. The tour will move on as soon as the name is ready.",
		advanceOnInput: true,
	},
	{
		id: "pedigree-details-continue",
		path: "/new-pedigree",
		title: "Continue to parent selection",
		description:
			"The default model is ready. You can review it above, then use this button to move to the Parents step.",
		advanceOnTargetClick: true,
	},
	{
		id: "pedigree-sire-input",
		path: "/new-pedigree",
		title: "Choose a sire",
		description:
			"Start with the sire search here. The tour will continue when the paternal line is complete.",
		advanceOnEvent: "onboarding:pedigree-sire-complete",
		align: "right",
	},
	{
		id: "pedigree-dam-input",
		path: "/new-pedigree",
		title: "Choose a dam",
		description:
			"Then choose the dam. The tour will continue when the maternal line is complete.",
		advanceOnEvent: "onboarding:pedigree-dam-complete",
		align: "right",
	},
	{
		id: "pedigree-review-button",
		path: "/new-pedigree",
		title: "Review the report",
		description:
			"Once both family lines are complete, review the selected horses, model, and credit cost.",
		advanceOnTargetClick: true,
		align: "right",
	},
	{
		id: "pedigree-generate-button",
		path: "/new-pedigree",
		title: "Generate the report",
		description:
			"When the details are complete, generate the report here. The welcome popup stops after the first successful pedigree creation.",
		align: "right",
		completeOnEvent: "onboarding:pedigree-report-created",
	},
];

const OnboardingContext = createContext({
	startTour: () => {},
	isTourAvailable: false,
});

function WelcomeModal({
	isOpen,
	credits,
	onStartTour,
	onClose,
	onHidePermanently,
}) {
	return (
		<Modal isOpen={isOpen} className="onboarding-modal">
			<div className="onboarding-modal__content">
				<div className="onboarding-modal__badge">Free Credits</div>
				<h2 className="subtitle is-black">🎉 Welcome to ChampionBred</h2>
				<p className="text is-black">
					You’re all set to explore your first report, and you already have{" "}
					<span className="onboarding-modal__credits">
						{credits} free credits
					</span>{" "}
					ready to use.
				</p>
				<p className="text is-black">
					Take the quick guided tour now, or come back to it later from the
					header.
				</p>
				<div className="onboarding-modal__actions">
					<button className="button" onClick={onStartTour}>
						Let&apos;s go
					</button>
					<button className="button button--secondary" onClick={onClose}>
						Later
					</button>
				</div>
				<button
					type="button"
					className="onboarding-modal__link"
					onClick={onHidePermanently}
				>
					Don&apos;t show this message again
				</button>
			</div>
		</Modal>
	);
}

function TourOverlay({ step, index, total, targetRect, onNext, onDismiss }) {
	const defaultLeft = 20;
	const defaultTop = 100;
	const cardWidth = 360;
	const cardHeight = 240;
	const cardGap = 18;
	const viewportPadding = 20;

	const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

	const rightAlignedCardStyle =
		step.align === "right" && targetRect
			? {
					left:
						targetRect.right + cardGap + cardWidth <=
						window.innerWidth - viewportPadding
							? targetRect.right + cardGap
							: Math.max(
									viewportPadding,
									targetRect.left - cardWidth - cardGap
							  ),
					top: clamp(
						targetRect.top + targetRect.height / 2 - cardHeight / 2,
						viewportPadding,
						window.innerHeight - cardHeight - viewportPadding
					),
			  }
			: null;

	const defaultTargetCardStyle = targetRect
		? {
				top: Math.min(
					targetRect.bottom + cardGap,
					window.innerHeight - cardHeight - viewportPadding
				),
				left: clamp(
					targetRect.left,
					viewportPadding,
					window.innerWidth - cardWidth - viewportPadding
				),
		  }
		: null;

	const cardStyle = targetRect
		? {
				position: "fixed",
				...(rightAlignedCardStyle || defaultTargetCardStyle),
		  }
		: {
				position: "fixed",
				top: defaultTop,
				left: defaultLeft,
		  };

	return (
		<div className="onboarding-tour">
			{targetRect ? null : <div className="onboarding-tour__shade" />}
			{targetRect ? (
				<div
					className="onboarding-tour__highlight"
					style={{
						top: targetRect.top - 8,
						left: targetRect.left - 8,
						width: targetRect.width + 16,
						height: targetRect.height + 16,
					}}
				/>
			) : null}
			<div className="onboarding-tour__card" style={cardStyle}>
				<p className="onboarding-tour__step">
					Step {index + 1} of {total}
				</p>
				<h3 className="subtitle is-black mb-10">{step.title}</h3>
				<p className="text is-black mb-20">{step.description}</p>
				<div className="onboarding-tour__actions">
					{step.primaryLabel ? (
						<button className="button" onClick={onNext}>
							{step.primaryLabel}
						</button>
					) : null}
					<button className="button button--secondary" onClick={onDismiss}>
						Dismiss
					</button>
				</div>
			</div>
		</div>
	);
}

export function OnboardingProvider({ children }) {
	const user = useContext(UserContext);
	const navigate = useNavigate();
	const location = useLocation();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isTourOpen, setIsTourOpen] = useState(false);
	const [currentStepIndex, setCurrentStepIndex] = useState(0);
	const [targetRect, setTargetRect] = useState(null);
	const dismissedThisSessionRef = useRef(false);
	const activeUserIdRef = useRef(null);

	useEffect(() => {
		if (user?.id !== activeUserIdRef.current) {
			activeUserIdRef.current = user?.id || null;
			dismissedThisSessionRef.current = false;
		}
	}, [user?.id]);

	const persistPreference = useCallback(async (value) => {
		try {
			const updatedUser = await userService.updateOnboarding({
				show_onboarding_welcome: value,
			});
			authenticationService.updateUserDetails(updatedUser);
		} catch (error) {
			console.error("Error updating onboarding preference", error);
		}
	}, []);

	const closeWelcomeModal = useCallback(async () => {
		setIsModalOpen(false);
		dismissedThisSessionRef.current = true;
		await persistPreference(true);
	}, [persistPreference]);

	const hideWelcomeModalForever = useCallback(async () => {
		setIsModalOpen(false);
		dismissedThisSessionRef.current = true;
		await persistPreference(false);
	}, [persistPreference]);

	const startTour = useCallback(() => {
		setCurrentStepIndex(0);
		setIsModalOpen(false);
		setIsTourOpen(true);
		dismissedThisSessionRef.current = true;
	}, []);

	const dismissTour = useCallback(() => {
		setIsTourOpen(false);
		setTargetRect(null);
	}, []);

	useEffect(() => {
		if (!user) {
			setIsModalOpen(false);
			setIsTourOpen(false);
			return;
		}

		const shouldAutoShow =
			user.show_onboarding_welcome &&
			!user.has_completed_onboarding &&
			!dismissedThisSessionRef.current;

		if (shouldAutoShow) {
			setIsModalOpen(true);
		}
	}, [user]);

	useEffect(() => {
		if (!isTourOpen) {
			return undefined;
		}

		const activeStep = TOUR_STEPS[currentStepIndex];
		if (
			activeStep.advanceOnPath &&
			location.pathname === activeStep.advanceOnPath
		) {
			setCurrentStepIndex((prev) =>
				prev < TOUR_STEPS.length - 1 ? prev + 1 : prev
			);
			return undefined;
		}

		if (
			activeStep.path &&
			activeStep.autoNavigate !== false &&
			location.pathname !== activeStep.path
		) {
			navigate(activeStep.path);
			return undefined;
		}

		let targetElement = null;
		const advanceStep = () => {
			setCurrentStepIndex((prev) =>
				prev < TOUR_STEPS.length - 1 ? prev + 1 : prev
			);
		};
		const handleTargetClick = () => advanceStep();
		const handleTargetInput = (event) => {
			if (String(event.target?.value || "").trim()) {
				advanceStep();
			}
		};
		const unbindTarget = () => {
			if (!targetElement) return;
			targetElement.removeEventListener("click", handleTargetClick);
			targetElement.removeEventListener("input", handleTargetInput);
		};
		const syncTarget = () => {
			const nextTarget = document.querySelector(
				`[data-tour-id="${activeStep.id}"]`
			);
			if (nextTarget !== targetElement) {
				unbindTarget();
				targetElement = nextTarget;
				if (targetElement && activeStep.advanceOnTargetClick) {
					targetElement.addEventListener("click", handleTargetClick);
				}
				if (targetElement && activeStep.advanceOnInput) {
					targetElement.addEventListener("input", handleTargetInput);
				}
			}
			setTargetRect(
				targetElement ? targetElement.getBoundingClientRect() : null
			);
		};
		const timer = window.setTimeout(syncTarget, 200);
		const observer = new MutationObserver(syncTarget);

		syncTarget();
		observer.observe(document.body, { childList: true, subtree: true });
		window.addEventListener("resize", syncTarget);
		window.addEventListener("scroll", syncTarget, true);

		let handleAdvanceEvent;
		if (activeStep.advanceOnEvent) {
			handleAdvanceEvent = () => advanceStep();
			window.addEventListener(activeStep.advanceOnEvent, handleAdvanceEvent);
		}

		let handleCompletion;
		if (activeStep.completeOnEvent) {
			handleCompletion = () => {
				dismissTour();
			};
			window.addEventListener(activeStep.completeOnEvent, handleCompletion);
		}

		return () => {
			unbindTarget();
			observer.disconnect();
			if (activeStep.advanceOnEvent && handleAdvanceEvent) {
				window.removeEventListener(
					activeStep.advanceOnEvent,
					handleAdvanceEvent
				);
			}
			if (activeStep.completeOnEvent && handleCompletion) {
				window.removeEventListener(
					activeStep.completeOnEvent,
					handleCompletion
				);
			}
			window.removeEventListener("resize", syncTarget);
			window.removeEventListener("scroll", syncTarget, true);
			window.clearTimeout(timer);
		};
	}, [currentStepIndex, dismissTour, isTourOpen, location.pathname, navigate]);

	const handleNext = useCallback(() => {
		const activeStep = TOUR_STEPS[currentStepIndex];
		if (
			activeStep.advanceOnPath &&
			location.pathname !== activeStep.advanceOnPath
		) {
			return;
		}

		if (currentStepIndex === TOUR_STEPS.length - 1) {
			dismissTour();
			return;
		}

		const nextStepIndex = currentStepIndex + 1;
		setCurrentStepIndex(nextStepIndex);
		const nextStep = TOUR_STEPS[nextStepIndex];
		if (
			nextStep.path &&
			nextStep.autoNavigate !== false &&
			location.pathname !== nextStep.path
		) {
			navigate(nextStep.path);
		}
	}, [currentStepIndex, dismissTour, location.pathname, navigate]);

	const contextValue = useMemo(
		() => ({
			startTour,
			isTourAvailable: Boolean(user),
		}),
		[startTour, user]
	);

	return (
		<OnboardingContext.Provider value={contextValue}>
			{children}
			<WelcomeModal
				isOpen={isModalOpen}
				credits={user?.credits || 0}
				onStartTour={startTour}
				onClose={closeWelcomeModal}
				onHidePermanently={hideWelcomeModalForever}
			/>
			{isTourOpen ? (
				<TourOverlay
					step={TOUR_STEPS[currentStepIndex]}
					index={currentStepIndex}
					total={TOUR_STEPS.length}
					targetRect={targetRect}
					onNext={handleNext}
					onDismiss={dismissTour}
				/>
			) : null}
		</OnboardingContext.Provider>
	);
}

export function useOnboarding() {
	return useContext(OnboardingContext);
}
